import React from "react";
import "react-native-gesture-handler";
import {
  AppRegistry,
  Platform,
  Keyboard,
  Alert,
  Dimensions,
  View,
  Text,
  TextInput,
} from "react-native";

import { SafeAreaProvider } from "react-native-safe-area-context";
import {
  getStateFromPath,
  getPathFromState,
  NavigationContainer,
} from "@react-navigation/native";
import { createDrawerNavigator } from "@react-navigation/drawer";
import { AuthStack } from "./stacks/auth-stack";

import ComponentsFactory from "./pages/ComponentsFactory";

//import RNIap, {
//  purchaseErrorListener,
//  purchaseUpdatedListener,
//} from "react-native-iap";
import Controller from "./controller";

import ActivitiesPage from "./pages/v3/activities/ActivitiesPage";
import AnimationPage from "./pages/v3/animation/AnimationPage";
import AppointmentPage from "./pages/v3/appointment/AppointmentPage";
import EditActivityPage from "./pages/v3/activities/EditActivityPage";
import EditSetPage from "./pages/v3/activities/groups/EditSetPage";
import EditTagPage from "./pages/v3/tags/EditTagPage";
import OnboardingAnimPage from "./pages/v3/animation/OnboardingAnimPage";
import OnboardingInfoPage from "./pages/v3/animation/info/OnboardingInfoPage";
import PageError from "./pages/error";
import PageFeed from "./pages/feed";
import PageOffline from "./pages/offline";
import PagePay from "./pages/pay";
import PageTerms from "./pages/terms";
import PageWelcome from "./pages/welcome";
import ProfilePage from "./pages/v3/more/ProfilePage";
import SurveyPage from "./pages/v3/survey/SurveyPage";
import TagActivitiesPage from "./pages/v3/tags/tag-activities/TagActivitiesPage";
import TagsPage from "./pages/v3/tags/TagsPage";
import TrackDetailPage from "./pages/v3/journal/edit/TrackDetailPage";
import TrackTutorialPage from "./pages/v3/home/gated-product/track-tutorial/TrackTutorialPage";
import PageLogin from "./pages/login/login";
import PageLoginErr from "./pages/loginerr";

import ErrorView from "./pages/v3/error/errorview";

import ApiService from "./api-service";

import HealthInt from "./services/health/health";
import CalendarInt from "./services/calendar";
import LocationInt from "./services/location/location";
//import PaymentsInt from "./services/payments/payments";
import CodePushInt from "./services/codepush/codepush";
import AppConfig from "./config/AppConfig";
import { isWeb } from "./helpers/utils";
import { AppProvider } from "./appContext";
import appStyles from "./pages/appStyles";

import NavTheme from "./styles/NavTheme";
import MorePageLink from "./pages/v3/more/MorePageLink";
import NativeHelper from "./pages/v3/native/NativeHelper";

import { enableScreens } from "react-native-screens";
import appPages from "./appPages";
enableScreens();

// Enable code push for specific platforms
const enableCodePush = Platform.select({
  web: false,
  default: true,
});

const CODEPUSH_DEPLOYMENT_KEY = Platform.select({
  ios: "Tvue0ZIebLCX34WKlhqda6ss4C5kzcwdzsVr4",
  android: "_IZRgNYZ0iBrmsZzTFPwwI25NGtQH8HvgYs7w",
});

const SUBSCRIPTIONS = Platform.select({
  ios: [
    "mana.ai.hours.subscription.monthly",
    "mana.ai.hours.subscription.yearly",
  ],
  android: [
    "mana.ai.hours.subscription.monthly",
    "mana.ai.hours.subscription.yearly",
  ],
});

let resetState = null;

if (!isWeb) {
  Text.defaultProps = Text.defaultProps || {};
  Text.defaultProps.allowFontScaling = false;
  TextInput.defaultProps = TextInput.defaultProps || {};
  TextInput.defaultProps.allowFontScaling = false;
}

const offlineEnabled = true; //!isWeb;
let Services = {
  healthIntegration: new HealthInt(),
  locationIntegration: new LocationInt(),
  calendarIntegration: new CalendarInt(),
  apiService: new ApiService(),
  paymentsIntegration: null, //new PaymentsInt(this.apiService),
};

const controller = new Controller(Services, offlineEnabled, null);

class App extends React.PureComponent {
  constructor(props) {
    super(props);

    this.purchaseUpdateSubscription = null;
    this.purchaseErrorSubscription = null;

    this.calendarEventsToRetrieve = 300;
    this.sleepDaysToRetrieve = 7; //TODO: initial call to get history, small intervals otherwise (since last known timestamp)

    this.healthIntegration = Services.healthIntegration;
    this.locationIntegration = Services.locationIntegration;
    this.calendarIntegration = Services.calendarIntegration;
    this.apiService = Services.apiService;
    this.paymentsIntegration = Services.paymentsIntegration;
    resetState = this.resetState;

    this.controller = controller;
    controller.onPageChange = this.onPageChange.bind(this);
    controller.navigation = props.navigation;

    this.onKeyboardWillShow = null;
    this.onKeyboardWillHide = null;

    this.lastTrackedPageId = null;

    this.useTimerRefresh = false;
    this.timedRefresh = null;
    this.timedUserData = null;

    //const { testing_preferences } = controller.state?.settings || {};
    const uiVersion = "v3";

    // PAGES BY VERSION
    this.versionPages = {
      EditElementPage: ComponentsFactory("EditElementPage", uiVersion),
      ElementsPage: ComponentsFactory("ElementsPage", uiVersion),
      Footer: ComponentsFactory("Footer", uiVersion),
      ResolutionDetailPage: ComponentsFactory(
        "ResolutionDetailPage",
        uiVersion,
      ),
      ResolutionPage: ComponentsFactory("ResolutionPage", uiVersion),
      ResolutionGemsPage: ComponentsFactory("ResolutionGemsPage", uiVersion),
      ArchiveElementsPage: ComponentsFactory("ArchiveElementsPage", uiVersion),
      ShareElementPage: ComponentsFactory("ShareElementPage", uiVersion),

      TrackHomePage: ComponentsFactory("TrackHomePage", uiVersion),
      TrackDetailsPage: ComponentsFactory("TrackDetailsPage", uiVersion),
      LastWeekPage: ComponentsFactory("LastWeekPage", uiVersion),
      HomePage: ComponentsFactory("HomePage", uiVersion),
      ReflectionPage: ComponentsFactory("ReflectionPage", uiVersion),
      ReflectionEditPage: ComponentsFactory("ReflectionEditPage", uiVersion),

      RoutinesPage: ComponentsFactory("RoutinesPage", "v3"),
      EditRoutinePage: ComponentsFactory("EditRoutinePage", "v3"),

      PageMore: ComponentsFactory("PageMore", uiVersion),
      SettingsPage: ComponentsFactory("SettingsPage", uiVersion),

      DesktopSyncPage: ComponentsFactory("DesktopSyncPage", uiVersion),
      HealthSyncPage: ComponentsFactory("HealthSyncPage", uiVersion),
      CalendarSyncPage: ComponentsFactory("CalendarSyncPage", uiVersion),
      ChromeSyncPage: ComponentsFactory("ChromeSyncPage", uiVersion),
      SlackSyncPage: ComponentsFactory("SlackSyncPage", uiVersion),

      EnabledUsersPage: ComponentsFactory("EnabledUsersPage", uiVersion),
      InviteFriendsPage: ComponentsFactory("InviteFriendsPage", uiVersion),
      KnowledgeBasePage: ComponentsFactory("KnowledgeBasePage", uiVersion),
      GetStartedPage: ComponentsFactory("GetStartedPage", uiVersion),
      HelpPage: ComponentsFactory("HelpPage", uiVersion),
      ImpersonatePage: ComponentsFactory("ImpersonatePage", uiVersion),

      DashboardPage: ComponentsFactory("DashboardPage", uiVersion),

      ReportsPage: ComponentsFactory("ReportsPage", "v3"),
      MyReportsPage: ComponentsFactory("MyReportsPage", "v3"),
      SnoozedCardsPage: ComponentsFactory("SnoozedCardsPage", "v3"),

      PaymentsPage: ComponentsFactory("PaymentsPage", uiVersion),
      TestingPage: ComponentsFactory("TestingPage", uiVersion),
      CoachingRoomPage: ComponentsFactory("CoachingRoomPage", uiVersion),
    };

    if (AppConfig.USE_DRAWER) {
      this.props.navigation.setOptions({
        gestureEnabled: false,
        swipeEnabled: false,
      });
      this.isDrawerEnabled = false;
    }

    this.editScreens = ["confirm", "sleep", "editelement"];
    this.pages = {
      welcome: AppConfig.USE_NAVIGATION
        ? undefined
        : new PageWelcome({ controller: this.controller }),
      terms: AppConfig.USE_NAVIGATION
        ? undefined
        : new PageTerms({ controller: this.controller }),
      login: AppConfig.USE_NAVIGATION
        ? undefined
        : new PageLogin({ controller: this.controller }),
      loginerr: AppConfig.USE_NAVIGATION
        ? undefined
        : new PageLoginErr({ controller: this.controller }),
      error: AppConfig.USE_NAVIGATION
        ? undefined
        : new PageError({ controller: this.controller }),
      more: AppConfig.USE_NAVIGATION
        ? undefined
        : new this.versionPages.PageMore({ controller: this.controller }),
      settings: new this.versionPages.SettingsPage({
        controller: this.controller,
      }),
      offline: new PageOffline({ controller: this.controller }),
      feed: new PageFeed({ controller: this.controller }),
      pay: new PagePay({ controller: this.controller }),
      animation: new AnimationPage({ controller: this.controller }),
      onboarding: AppConfig.USE_NAVIGATION
        ? undefined
        : new OnboardingAnimPage({ controller: this.controller }),
      tags: new TagsPage({ controller: this.controller }),
      home_card: new this.versionPages.HomePage({
        controller: this.controller,
      }),
      sets_reflection: new this.versionPages.ReflectionPage({
        controller: this.controller,
      }),
      sets_reflection_detail: new this.versionPages.ReflectionEditPage({
        controller: this.controller,
      }),
      resolution: new this.versionPages.ResolutionPage({
        controller: this.controller,
      }),
      resolution_detail: new this.versionPages.ResolutionDetailPage({
        controller: this.controller,
      }),
      resolution_gems: new this.versionPages.ResolutionGemsPage({
        controller: this.controller,
      }),
      journal: new this.versionPages.TrackHomePage({
        controller: this.controller,
      }),
      edit_journal: new TrackDetailPage({ controller: this.controller }),
      edit_tag: new EditTagPage({ controller: this.controller }),
      edit_activity: new EditActivityPage({ controller: this.controller }),
      tag_activities: new TagActivitiesPage({ controller: this.controller }),
      activities: new ActivitiesPage({ controller: this.controller }),
      onboarding_template: new this.versionPages.LastWeekPage({
        controller: this.controller,
        templateName: "Onboarding",
      }),
      average_template: new this.versionPages.LastWeekPage({
        controller: this.controller,
        templateName: "AutoTrack",
      }),
      appointment: new AppointmentPage({ controller: this.controller }),
      survey: new SurveyPage({ controller: this.controller }),
      knowledge_base: new this.versionPages.KnowledgeBasePage({
        controller: this.controller,
      }),
      track_tutorial: new TrackTutorialPage({ controller: this.controller }),
      edit_set: new EditSetPage({ controller: this.controller }),
      impersonate: new this.versionPages.ImpersonatePage({
        controller: this.controller,
      }),
      get_started: new this.versionPages.GetStartedPage({
        controller: this.controller,
      }),
      enabled_users: new this.versionPages.EnabledUsersPage({
        controller: this.controller,
      }),
      invite_friends: new this.versionPages.InviteFriendsPage({
        controller: this.controller,
      }),
      testing: new this.versionPages.TestingPage({
        controller: this.controller,
      }),
      profile: new ProfilePage({ controller: this.controller }),
      help: new this.versionPages.HelpPage({ controller: this.controller }),
      elements_v2: new this.versionPages.ElementsPage({
        controller: this.controller,
      }),
      edit_element: new this.versionPages.EditElementPage({
        controller: this.controller,
      }),
      share_element: new this.versionPages.ShareElementPage({
        controller: this.controller,
      }),
      archive_elements: new this.versionPages.ArchiveElementsPage({
        controller: this.controller,
      }),
      routines: new this.versionPages.RoutinesPage({
        controller: this.controller,
      }),
      edit_routine: new this.versionPages.EditRoutinePage({
        controller: this.controller,
      }),
      sent_reports: new this.versionPages.MyReportsPage({
        controller: this.controller,
      }),
      reports: new this.versionPages.MyReportsPage({
        controller: this.controller,
      }),
      view_report: new this.versionPages.ReportsPage({
        controller: this.controller,
      }),
      snoozed_cards: new this.versionPages.SnoozedCardsPage({
        controller: this.controller,
      }),
      dashboard: new this.versionPages.DashboardPage({
        controller: this.controller,
      }),
    };

    this.controller.setDefinedPages(Object.keys(this.pages));
  }

  componentDidMount = async () => {
    if (Platform.OS !== "web") {
      if (enableCodePush) {
        this.initCodePush();
      }

      if (Platform.OS === "ios") {
        //2020.11 - disable android payments
        this.initPurchaseListeners();
      }
    }
    this.controller.updateState = this.updateState.bind(this);

    console.disableYellowBox = true;
    this.updateState();

    this.retrieveUserDataDelayed(false);

    const app = this;
    this.controller.refreshIntegrations =
      this.retrieveUserDataDelayed.bind(this);

    if (this.useTimerRefresh) {
      this.timedRefresh = setInterval(() => this.getItems(true), 5 * 60 * 1000);
    }
    this.timedUserData = setInterval(
      () => this.retrieveUserData(),
      15 * 60 * 1000, //TODO: use configured interval,
    );

    this.initTrackingService();
  };

  initTrackingService() {
    if (
      this.controller.state.currentUser != null
    ) {
      // Indentify the current user
      this.controller.tracking.identify(this.controller.state.currentUser);
    }
  }

  componentDidUpdate() {
    if (!AppConfig.USE_NAVIGATION) {
      this.pages.feed.onPageUpdate();

      this.retrieveUserDataDelayed(false);
      this.initTrackingService();
    }

    if (window.initialEmbeddedLogin) {
      const params = window.initialEmbeddedLogin;
      window.initialEmbeddedLogin = null;
      this.controller.setState({
        installId: params.installId,
        currentUser: params.email,
        accessToken: params.token,
        picture: params.picture,
      });
    }
  }

  initEmbedded() {
    this.controller.updateEmbeddedSettings();

    if (isWeb) {
      NativeHelper.configureCallback(
        controller,
        this.props.navigation || this.controller.navigation,
      );
    }
  }

  retrieveUserDataDelayed(forceUpdate) {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.retrieveUserData(forceUpdate);
        if (resolve) resolve();
      }, 1000);
    });
  }

  retrieveUserData(forceUpdate) {
    if (this.controller.state?.currentUser == null) return;

    if (
      this.controller.state?.impersonate == null ||
      this.controller.state?.impersonate ===
        this.controller.state?.currentUser ||
      this.controller.state?.settings?.integrations_redirected
    ) {
      console.log("Retrieve data");
      const calendarTypes = [];
      const hasCalendarGoogle =
        this.controller?.state?.settings?.integrations?.calendar_google;
      if (hasCalendarGoogle) calendarTypes.push("google");

      const hasCalendarNative =
        !isWeb &&
        this.controller?.state?.settings?.integrations?.calendar_native;
      if (hasCalendarNative) calendarTypes.push("native");

      if (calendarTypes.length > 0) {
        this.calendarIntegration
          .retrieveCalendarEvents(
            this.controller,
            calendarTypes,
            forceUpdate,
            this.calendarEventsToRetrieve,
          )
          .then(() => console.log("Calendars retrieved."));
      }

      const hasHealth = this.controller?.state?.settings?.integrations?.health;
      if (hasHealth) {
        this.healthIntegration.retrieveHealthData(
          this.controller,
          forceUpdate,
          this.sleepDaysToRetrieve,
        );
      }

      const hasLocation =
        this.controller?.state?.settings?.integrations?.location;
      if (hasLocation) {
        this.locationIntegration.retrieveLocationData(this.controller);
      }
    }
  }

  UNSAFE_componentWillMount() {
    this.initEmbedded();

    const that = this;
    if (this.onKeyboardWillShow == null) {
      this.onKeyboardWillShow = function (e) {
        that.pages.feed.keyboardWillShow(e);
      };
      Keyboard.addListener("keyboardWillShow", this.onKeyboardWillShow);
    }
    if (this.onKeyboardWillHide == null) {
      this.onKeyboardWillHide = function (e) {
        that.pages.feed.keyboardWillHide(e);
      };
      Keyboard.addListener("keyboardWillHide", this.onKeyboardWillHide);
    }
  }

  componentWillUnmount() {
    this.removePurchaseListeners();
    if (this.timedRefresh != null) clearInterval(this.timedRefresh);
    if (this.timedUserData != null) clearInterval(this.timedUserData);
  }

  render() {
    const { controller } = this;
    if (controller == null) return null;

    const { state } = controller;
    let { currentScreen, selectedPage } = state;

    if (AppConfig.USE_DRAWER) {
      if (state.currentUser == null && this.isDrawerEnabled) {
        this.props.navigation.setOptions({
          gestureEnabled: false,
          swipeEnabled: false,
        });
        this.isDrawerEnabled = false;
      } else if (state.currentUser != null && !this.isDrawerEnabled) {
        this.props.navigation.setOptions({
          gestureEnabled: true,
          swipeEnabled: true,
        });
        this.isDrawerEnabled = true;
      }
    }
    if (state.accessToken == null && currentScreen === "feed") {
      currentScreen = "welcome";
    }

    if (currentScreen === "welcome" || currentScreen === "login") {
      if (state.currentUser !== null) {
        currentScreen = "feed";
      } else if (isWeb && window.location.hash) {
        const loginScreen = AppConfig.USE_NAVIGATION
          ? new PageLogin({ controller: controller })
          : this.pages.login;
        if (loginScreen != null) {
          loginScreen.parse();
        }
      }
    }

    if (isWeb) {
      NativeHelper.initFlags(controller);
    }

    if (
      currentScreen === "feed" &&
      selectedPage != null &&
      selectedPage.startsWith("rn:")
    ) {
      currentScreen = selectedPage.substring("rn:".length);
    }

    if (
      ((currentScreen === "feed" &&
        (selectedPage === "" ||
          selectedPage === "home" ||
          (selectedPage != null && selectedPage.includes("home_card")))) ||
        currentScreen === "home") &&
      this.controller.getUIVersion("feed") === "v2"
    ) {
      currentScreen = "home_card";
    }

    if (
      this.controller.state.impersonate != null &&
      currentScreen === appPages.onboardingInfoPage
    ) {
      currentScreen = "home_card";
    }

    const page =
      this.pages[currentScreen] != null
        ? this.pages[currentScreen]
        : this.pages.error;

    console.log(
      new Date().toISOString() +
        " Rendering " +
        (this.controller.hasNavigation() ? "" : currentScreen),
    );
    const renderAsComponent = this.isReactComponent(page);
    // or: const renderAsComponent = page.componentDidMount != null || page.componentDidUpdate != null;

    const pageId =
      currentScreen === "feed"
        ? currentScreen + " " + selectedPage
        : currentScreen;
    if (pageId !== this.lastTrackedPageId) {
      this.lastTrackedPageId = pageId;
      //this.controller.tracking.screen(pageId);
      this.controller.tracking.track("Show " + pageId);
    }

    if (this.controller.hasNavigation()) {
      return (
        <AuthStack
          controller={controller}
          isLoggedIn={this.controller.state.currentUser}
          pages={this.versionPages}
        />
      );
    }

    // return <RootStack controller={controller} />;

    if (renderAsComponent) {
      return (
        <View
          style={[
            appStyles.container,
            isWeb ? { overflowX: "hidden", backgroundColor: "#FFF" } : {},
          ]}
        >
          {page instanceof PageWelcome && (
            <PageWelcome controller={controller} />
          )}
          {page instanceof PageTerms && <PageTerms controller={controller} />}
          {page instanceof ActivitiesPage && (
            <ActivitiesPage controller={controller} />
          )}
          {page instanceof EditActivityPage && (
            <EditActivityPage controller={controller} />
          )}
          {page instanceof TagsPage && <TagsPage controller={controller} />}
          {page instanceof EditTagPage && (
            <EditTagPage controller={controller} />
          )}
          {page instanceof TagActivitiesPage && (
            <TagActivitiesPage controller={controller} />
          )}
          {page instanceof this.versionPages.TrackHomePage && (
            <this.versionPages.TrackHomePage controller={controller} />
          )}
          {page instanceof TrackDetailPage && (
            <TrackDetailPage controller={controller} />
          )}
          {page instanceof AnimationPage && (
            <AnimationPage controller={controller} />
          )}
          {page instanceof OnboardingInfoPage && (
            <OnboardingInfoPage controller={controller} />
          )}
          {page instanceof this.versionPages.HomePage && (
            <this.versionPages.HomePage controller={controller} />
          )}
          {page instanceof this.versionPages.ReflectionPage && (
            <this.versionPages.ReflectionPage controller={controller} />
          )}
          {page instanceof this.versionPages.ReflectionEditPage && (
            <this.versionPages.ReflectionEditPage controller={controller} />
          )}
          {page instanceof this.versionPages.ResolutionPage && (
            <this.versionPages.ResolutionPage controller={controller} />
          )}
          {page instanceof this.versionPages.ResolutionDetailPage && (
            <this.versionPages.ResolutionDetailPage controller={controller} />
          )}
          {page instanceof this.versionPages.ResolutionGemsPage && (
            <this.versionPages.ResolutionGemsPage controller={controller} />
          )}
          {page instanceof OnboardingAnimPage && (
            <OnboardingAnimPage controller={controller} />
          )}
          {page instanceof this.versionPages.LastWeekPage && (
            <this.versionPages.LastWeekPage
              controller={controller}
              templateName={page.templateName}
            />
          )}
          {page instanceof AppointmentPage && (
            <AppointmentPage controller={controller} />
          )}
          {page instanceof SurveyPage && <SurveyPage controller={controller} />}
          {page instanceof this.versionPages.KnowledgeBasePage && (
            <this.versionPages.KnowledgeBasePage controller={controller} />
          )}
          {page instanceof TrackTutorialPage && (
            <TrackTutorialPage controller={controller} />
          )}
          {page instanceof this.versionPages.PageMore && (
            <this.versionPages.PageMore controller={controller} />
          )}
          {page instanceof this.versionPages.SettingsPage && (
            <this.versionPages.SettingsPage controller={controller} />
          )}
          {page instanceof EditSetPage && (
            <EditSetPage controller={controller} />
          )}
          {page instanceof this.versionPages.ImpersonatePage && (
            <this.versionPages.ImpersonatePage controller={controller} />
          )}
          {page instanceof this.versionPages.EnabledUsersPage && (
            <this.versionPages.EnabledUsersPage controller={controller} />
          )}
          {page instanceof this.versionPages.InviteFriendsPage && (
            <this.versionPages.InviteFriendsPage controller={controller} />
          )}
          {page instanceof this.versionPages.TestingPage && (
            <this.versionPages.TestingPage controller={controller} />
          )}
          {page instanceof ProfilePage && (
            <ProfilePage controller={controller} />
          )}
          {page instanceof this.versionPages.HelpPage && (
            <this.versionPages.HelpPage controller={controller} />
          )}
          {page instanceof this.versionPages.ElementsPage && (
            <this.versionPages.ElementsPage controller={controller} />
          )}
          {page instanceof this.versionPages.EditElementPage && (
            <this.versionPages.EditElementPage controller={controller} />
          )}
          {page instanceof this.versionPages.ShareElementPage && (
            <this.versionPages.ShareElementPage controller={controller} />
          )}
          {page instanceof this.versionPages.ArchiveElementsPage && (
            <this.versionPages.ArchiveElementsPage controller={controller} />
          )}
          {page instanceof this.versionPages.GetStartedPage && (
            <this.versionPages.GetStartedPage controller={controller} />
          )}
          {page instanceof this.versionPages.RoutinesPage && (
            <this.versionPages.RoutinesPage controller={controller} />
          )}
          {page instanceof this.versionPages.EditRoutinePage && (
            <this.versionPages.EditRoutinePage controller={controller} />
          )}
          {page instanceof this.versionPages.ReportsPage && (
            <this.versionPages.ReportsPage controller={controller} />
          )}
          {page instanceof this.versionPages.MyReportsPage && (
            <this.versionPages.MyReportsPage controller={controller} />
          )}
          {page instanceof this.versionPages.SnoozedCardsPage && (
            <this.versionPages.SnoozedCardsPage controller={controller} />
          )}
          {page instanceof this.versionPages.DashboardPage && (
            <this.versionPages.DashboardPage controller={controller} />
          )}
          {/*  {page instanceof this.versionPages.EditReportPage && (
            <this.versionPages.EditReportPage controller={controller} />
          )} */}
        </View>
      );
    } else {
      return page.render();
    }
  }

  isReactComponent(page) {
    return (
      page != null &&
      page.__proto__ != null &&
      page.__proto__.__proto__ != null &&
      page.__proto__.__proto__.isReactComponent != null
    );
  }

  updateState() {
    const state = this.controller.getState();
    const needsRefresh =
      state.currentUser != null &&
      this.apiService.token == null &&
      state.accessToken != null &&
      (state.selectedPage == null ||
        state.selectedPage.substring(0, 3) !== "rn:");

    if (
      this.apiService.token != null &&
      state.accessToken == null &&
      state.installId != null
    ) {
      console.log("Logging out...");
      this.apiService.token = null;
      this.controller.logout();
    }

    this.apiService.setUser(
      state.currentUser,
      state.accessToken,
      state.installId,
    );
    this.apiService.setImpersonate(state.impersonate);

    this.setState(state);

    if (needsRefresh) {
      this.getItems();
    }
  }

  onPageChange() {
    this.pages.feed.resetScroll();
  }

  getItems(withStats) {
    if (withStats) {
      this.controller.getItemsWithStats();
    } else {
      this.controller.getItems();
    }
  }

  initCodePush = async () => {
    try {
      await CodePush.sync({
        deploymentKey: CODEPUSH_DEPLOYMENT_KEY,
        updateDialog: false,
        installMode: CodePush.InstallMode.ON_NEXT_RESUME,
      });
    } catch (e) {
      console.log("Sync failed");
      console.log(e);
    }
  };

  initPurchaseListeners = async () => {
    try {
      this.purchaseUpdateSubscription = (purchase) => {};
      // purchaseUpdatedListener(
      //   (purchase: InAppPurchase | SubscriptionPurchase | ProductPurchase) => {
      //     console.log("purchaseUpdatedListener", purchase);
      //     const receipt = purchase.transactionReceipt;
      //     if (receipt) {
      //       this.controller.apiService
      //         .pay({ receipt: purchase.transactionReceipt })
      //         .then(async (result) => {
      //           console.log(result);
      //           if (result.status === "ok") {
      //             console.log("Transaction ok");
      //             if (Platform.OS === "ios") {
      //               await RNIap.finishTransactionIOS(purchase.transactionId);
      //             } else if (Platform.OS === "android") {
      //               await RNIap.consumePurchaseAndroid(purchase.purchaseToken);
      //             }
      //
      //             const purchases = await RNIap.getAvailablePurchases();
      //             console.log("Available purchases", JSON.stringify(purchases));
      //             this.controller.setPurchases(purchases);
      //           } else {
      //             Alert.alert(
      //               "Subscription purchase",
      //               `Payment failed, please contact us and include your receipt number ${purchase.transactionReceipt}`,
      //             );
      //           }
      //         });
      //     }
      //   },
      // );

      this.purchaseErrorSubscription = (error) => {};
      //purchaseErrorListener(
      //  (error: PurchaseError) => {
      //    console.warn("purchaseErrorListener", error);
      //  },
      //);

      console.log("Init payments");
      // const result = await RNIap.initConnection();
      // console.log("IAP", result);
      // if (Platform.OS === "android") {
      //   await RNIap.consumeAllItemsAndroid();
      // }

      // const products = await RNIap.getProducts(SUBSCRIPTIONS);
      // console.log("Products", JSON.stringify(products));
      //
      // const subscriptions = await RNIap.getSubscriptions(SUBSCRIPTIONS);
      // console.log("Subscriptions", JSON.stringify(subscriptions));
      // this.controller.setPayedSubscriptions(subscriptions);

      const purchases = []; //await RNIap.getAvailablePurchases();
      console.log("Available purchases", JSON.stringify(purchases));
      this.controller.setPurchases(purchases);
    } catch (e) {
      console.log("Failed payments initialization");
      console.log(e);
    }
  };

  removePurchaseListeners() {
    if (this.purchaseUpdateSubscription) {
      this.purchaseUpdateSubscription.remove();
      this.purchaseUpdateSubscription = null;
    }
    if (this.purchaseErrorSubscription) {
      this.purchaseErrorSubscription.remove();
      this.purchaseErrorSubscription = null;
    }
  }

  resetState() {
    if (this.controller != null) {
      this.controller.resetState(true);
      this.controller.getItems();
    }
  }
}

function reset() {
  if (resetState != null) resetState();
}

if (enableCodePush) {
  console.log("CodePush decorate");
  App = CodePushInt.decorate(App, CODEPUSH_DEPLOYMENT_KEY);
}

const prefix = isWeb
  ? ["https://app.mana.ai", "http://localhost:8080"]
  : Platform.OS === "android"
  ? ["mana://mana/"]
  : ["mana://"];
const ErrorBoundary = React.Fragment;

const DEEP_LINKED_PAGES = [
  "/" + appPages.receivedReports,
  "/" + appPages.sentReports,
  "/" + appPages.scheduleCall,
  "/" + appPages.integrationsV2,
];
const ROOT_PAGES = [
  "/feed",
  "/" + appPages.trackHome,
  "/" + appPages.reflectionPage,
  "/" + appPages.resolutionPage,
  "/" + appPages.receivedReports,
];
const linking = {
  prefixes: prefix,
  enabled: true,
  getStateFromPath: (path, options) => {
    const longPath = "/m" + (ROOT_PAGES.includes(path) ? "/mana" : "") + path;
    return getStateFromPath(longPath, options);
  },
  getPathFromState(state, config) {
    const path = getPathFromState(state, config);
    const qidx = path.indexOf("?");
    let shortPath = qidx >= 0 ? path.substring(2, qidx) : path.substring(2);
    if (
      !shortPath.startsWith("/mana/") &&
      !DEEP_LINKED_PAGES.includes(shortPath)
    )
      return "";
    if (shortPath.startsWith("/mana/")) shortPath = shortPath.substring(5);
    return shortPath;
  },
};
const Drawer = createDrawerNavigator();

const { width: WIDTH } = Dimensions.get("window");

const AppContainer = AppConfig.USE_DRAWER ? (
  <NavigationContainer linking={linking} theme={NavTheme}>
    <Drawer.Navigator
      drawerContent={(props) => MorePageLink(props, controller)}
      drawerType={"back"}
      drawerStyle={{ width: Math.min(WIDTH, 375) }}
      screenOptions={{ headerShown: false, header: () => null }}
    >
      <Drawer.Screen name="m" component={App} />
    </Drawer.Navigator>
  </NavigationContainer>
) : AppConfig.USE_NAVIGATION ? (
  <NavigationContainer linking={linking} theme={NavTheme}>
    <App uriPrefix={prefix} />
  </NavigationContainer>
) : (
  <App uriPrefix={prefix} />
);

const ManaApp = () => (
  <AppProvider
    value={{
      controller: controller,
    }}
  >
    <SafeAreaProvider>
      <ErrorBoundary FallbackComponent={ErrorView} onError={reset}>
        {AppContainer}
      </ErrorBoundary>
    </SafeAreaProvider>
  </AppProvider>
);

AppRegistry.registerComponent("Mana", () => ManaApp);

export default ManaApp;
